import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { appSettings } from '@portal/settings';
import { TranslatePipe, UserMenu } from 'eshava-shared';
import { filter, map } from 'rxjs';

import { NAVIGATION } from './navigation';
import { ProductContextService } from './product-context.service';
import { HelpButton } from '@portal/shared/ui/help-button/help-button';

/**
 * Top bar: brand and context on the left, the account on the right. **No menu here** -- the
 * navigation lives entirely in the sidebar, and the sidebar's collapse control moved into it.
 *
 * Appearance and language sit in the user menu rather than as buttons of their own. They are display
 * preferences someone sets once, and a permanent control for a once-a-year decision is weight the bar
 * does not need to carry.
 *
 * The product dropdown is `disabled` in the settings area rather than hidden. An element that
 * disappears and returns makes the bar jump and the context feel lost; the selection still applies,
 * it just is not applicable here.
 *
 * Native `<select>` on purpose: accessible, touch-capable and keyboard-capable without any of it
 * being built, and `<optgroup>` gives the grouping by subscription for free. Styled to read as part
 * of the bar rather than as a form field -- in a top bar this is a display of the current context
 * that happens to be changeable, not an input.
 */
@Component({
  selector: 'portal-top-bar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HelpButton, TranslatePipe, UserMenu],
  template: `
    <div class="top-bar__group">
      <img class="top-bar__logo" src="assets/eshava.svg" [alt]="'shell.logo.alt' | t" width="32" height="32" />

      <label class="top-bar__context">
        <span class="visually-hidden">{{ 'shell.context.label' | t }}</span>
        <!-- The selection is bound on the option, not as a value on the select: Angular applies the select's
             value before the looped options exist, so the browser falls back to the first one and the
             control shows something nobody chose. Invisible here while the list is empty, which is
             exactly how it would have reached production. -->
        <select class="top-bar__select" [disabled]="!contextApplies()" (change)="onProductChange($event)">
          @if (context.hasProducts()) {
            <option value="">{{ 'shell.context.placeholder' | t }}</option>
            @for (group of context.groups(); track group.subscriptionId) {
              <optgroup [label]="group.label">
                @for (product of group.products; track product.id) {
                  <option [value]="product.id" [selected]="context.selectedId() === product.id">{{ product.name }}</option>
                }
              </optgroup>
            }
          } @else {
            <option value="">{{ 'shell.context.empty' | t }}</option>
          }
        </select>
      </label>

      <portal-help-button helpKey="help.shell.context" />
    </div>

    <eshava-user-menu [appVersion]="version" />
  `,
  styles: `
    :host {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--space-4);
      height: var(--topbar-height);
      padding: 0 var(--space-3);
      background-color: var(--surface);
      border-bottom: 1px solid var(--border);
    }

    .top-bar__group {
      display: flex;
      align-items: center;
      gap: var(--space-3);
      min-width: 0;
    }

    /* The logo keeps its own colours in both themes -- it is a mark, not an icon, so it does not
       inherit the text colour the way everything in the sprite does. */
    .top-bar__logo {
      flex: none;
      width: 2rem;
      height: 2rem;
    }

    .top-bar__context {
      display: inline-flex;
      align-items: center;
      min-width: 0;
    }

    /* Light at rest, defined on interaction: a border and a filled background make the bar look like
       a form. The chevron the browser draws already says it can be changed. */
    .top-bar__select {
      min-width: 0;
      max-width: 20rem;
      padding: var(--space-1) var(--space-2);
      font: inherit;
      color: var(--text);
      background-color: transparent;
      border: 1px solid transparent;
      border-radius: var(--radius-sm);
      cursor: pointer;
      transition: background-color var(--duration) var(--ease), border-color var(--duration) var(--ease);
    }

    .top-bar__select:hover:not(:disabled) {
      background-color: var(--surface-raised);
      border-color: var(--border);
    }

    .top-bar__select:focus-visible {
      background-color: var(--surface-raised);
      border-color: var(--border-strong);
    }

    .top-bar__select:disabled {
      color: var(--text-subtle);
      cursor: default;
    }
  `
})
export class TopBar {
  /** Handed to the shared user menu, which cannot read this application's settings. */
  protected readonly version = appSettings.version;

  private readonly router = inject(Router);

  protected readonly context = inject(ProductContextService);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.router.url)
    ),
    { initialValue: this.router.url }
  );

  /**
   * Settings is account-wide, so the product selection does not apply there -- the dropdown is
   * disabled, not hidden. `NAVIGATION` is the single source for which areas need a product, so this
   * derives from the active URL rather than repeating the flag in the routes.
   */
  protected readonly contextApplies = computed(() => {
    const segment = this.url().split('?')[0].split('/').filter(Boolean)[0] ?? '';
    const area = NAVIGATION.find(entry => entry.path === segment);

    return (area?.requiresProduct ?? false) && this.context.hasProducts();
  });

  protected onProductChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.context.select(value === '' ? null : Number(value));
  }
}